// client/src/pages/EmailTemplatesSettingsPage.js
// Settings page for managing email templates

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getEmailTemplates,
  updateEmailTemplate,
  deleteEmailTemplate,
  getPlaceholdersForType
} from '../api/emailTemplates';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Modal from '../components/Modal';

function EmailTemplatesSettingsPage({ inTab = false }) {
  const { loading: authLoading } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    subject: '',
    body: '',
    is_auto_send_enabled: true
  });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Placeholders
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const { templates: fetchedTemplates } = await getEmailTemplates();
      setTemplates(fetchedTemplates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (template) => {
    setEditingTemplate(template);
    setEditFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      is_auto_send_enabled: template.is_auto_send_enabled
    });

    // Load placeholders for this template type
    const availablePlaceholders = getPlaceholdersForType(template.type);
    setPlaceholders(availablePlaceholders);
    setShowPlaceholders(false);

    setEditError('');
    setShowEditModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setEditLoading(true);
      setEditError('');

      // Validation
      if (!editFormData.name.trim()) {
        throw new Error('Template name is required');
      }
      if (!editFormData.subject.trim()) {
        throw new Error('Subject is required');
      }
      if (!editFormData.body.trim()) {
        throw new Error('Email body is required');
      }

      await updateEmailTemplate(editingTemplate.id, editFormData);

      // Refresh templates
      await fetchTemplates();

      // Close modal
      setShowEditModal(false);
      setEditingTemplate(null);

    } catch (err) {
      console.error('Error saving template:', err);
      setEditError(err.message || 'Failed to save template');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRevertToDefault = async (template) => {
    if (!window.confirm(`Revert "${template.name}" to system default? Your customizations will be lost.`)) {
      return;
    }

    try {
      await deleteEmailTemplate(template.id);
      await fetchTemplates();
    } catch (err) {
      console.error('Error reverting template:', err);
      setError(err.message || 'Failed to revert template');
    }
  };

  const insertPlaceholder = (placeholderKey) => {
    const placeholder = `{{${placeholderKey}}}`;
    setEditFormData({
      ...editFormData,
      body: editFormData.body + ' ' + placeholder
    });
  };

  const getTemplateTypeLabel = (type) => {
    const labels = {
      'appointment_request_received': 'Appointment Request Received',
      'appointment_scheduled': 'Appointment Scheduled',
      'appointment_rescheduled': 'Appointment Rescheduled',
      'quote_sent': 'Quote Sent',
      'quote_followup': 'Quote Follow-up',
      'quote_accepted': 'Quote Accepted',
      'quote_rejected': 'Quote Rejected',
      'quote_revision_requested': 'Quote Revision Requested',
      'invoice_issued': 'Invoice Issued',
      'payment_confirmation': 'Payment Confirmation'
    };
    return labels[type] || type;
  };

  const getAutoSendLabel = (template) => {
    const autoSendTypes = [
      'appointment_request_received',
      'quote_accepted',
      'quote_rejected',
      'quote_revision_requested'
    ];

    return autoSendTypes.includes(template.type);
  };


  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const containerClasses = inTab ? "" : "min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8";
  const wrapperClasses = inTab ? "" : "max-w-6xl mx-auto";
  const headerClasses = inTab ? "" : "mb-8";
  const titleClasses = inTab ? "text-xl font-bold text-primary-600 mb-2" : "text-3xl font-bold text-primary-700 mb-2";

  return (
    <div className={containerClasses}>
      <div className={wrapperClasses}>
        {!inTab && (
          <div className={headerClasses}>
            <h1 className={titleClasses}>Email Templates</h1>
            <p className="text-gray-600">
              Customize your email templates. Edit any template to create your personalized version.
            </p>
          </div>
        )}

        {error && <ErrorMessage message={error} />}

        {/* Templates Grid */}
        <div className="grid grid-cols-1 gap-6">
          {[...templates].sort((a, b) => {
            // Client emails first (a.category = 'client'), system emails after
            if (a.category === 'client' && b.category !== 'client') return -1;
            if (a.category !== 'client' && b.category === 'client') return 1;
            // Within same category, sort by pipeline_stage
            return (a.pipeline_stage || 0) - (b.pipeline_stage || 0);
          }).map((template) => {
            const isAutoSendType = getAutoSendLabel(template);

            return (
              <div
                key={template.id}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2 min-w-0">
                      <h3 className="text-xl font-semibold text-gray-900 break-words">
                        {template.name}
                      </h3>

                      {/* System Default vs Customized Badge */}
                      {template.is_system_default ? (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          System Default
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                          Customized
                        </span>
                      )}

                      {/* Auto-Send Badge */}
                      {isAutoSendType && (
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          template.is_auto_send_enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {template.is_auto_send_enabled ? 'Auto-Send ON' : 'Auto-Send OFF'}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 mb-3">
                      Type: {getTemplateTypeLabel(template.type)}
                    </p>

                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 min-w-0">
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-gray-600 uppercase">Subject:</span>
                        <p className="text-sm text-gray-800 mt-1 break-words">{template.subject}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-gray-600 uppercase">Body Preview:</span>
                        <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap line-clamp-3 break-words">
                          {template.body}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="ml-0 sm:ml-4 flex flex-col gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => handleEditClick(template)}
                      className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition duration-300 whitespace-nowrap"
                    >
                      Edit Template
                    </button>

                    {!template.is_system_default && (
                      <button
                        onClick={() => handleRevertToDefault(template)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition duration-300 whitespace-nowrap"
                      >
                        Revert to Default
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Edit Modal */}
        {showEditModal && editingTemplate && (
          <Modal
            onClose={() => {
              setShowEditModal(false);
              setEditingTemplate(null);
            }}
            title={`Edit Template: ${editingTemplate.name}`}
            maxWidth="max-w-4xl"
          >
            <div className="space-y-4">
              {editError && <ErrorMessage message={editError} />}

              {/* Info Banner */}
              {editingTemplate.is_system_default && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
                  You're editing a system default. Your changes will create a personalized version that overrides the default.
                </div>
              )}

              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={editLoading}
                />
              </div>

              {/* Auto-Send Toggle (only for auto-send types) */}
              {getAutoSendLabel(editingTemplate) && (
                <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Automatic Sending</p>
                    <p className="text-xs text-gray-600 mt-1">
                      When enabled, this email will be sent automatically when the trigger event occurs.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.is_auto_send_enabled}
                      onChange={(e) => setEditFormData({ ...editFormData, is_auto_send_enabled: e.target.checked })}
                      className="sr-only peer"
                      disabled={editLoading}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.subject}
                  onChange={(e) => setEditFormData({ ...editFormData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={editLoading}
                />
              </div>

              {/* Body */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Body <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPlaceholders(!showPlaceholders)}
                    className="text-xs text-primary-600 hover:text-primary-800"
                    disabled={editLoading}
                  >
                    {showPlaceholders ? 'Hide' : 'Show'} Placeholders
                  </button>
                </div>

                <textarea
                  value={editFormData.body}
                  onChange={(e) => setEditFormData({ ...editFormData, body: e.target.value })}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  disabled={editLoading}
                />

                {/* Placeholders Helper */}
                {showPlaceholders && placeholders.length > 0 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      Available Placeholders (click to insert):
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {placeholders.map((ph) => (
                        <div key={ph.key} className="flex items-start">
                          <button
                            type="button"
                            onClick={() => insertPlaceholder(ph.key)}
                            className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded font-mono mr-2 whitespace-nowrap"
                            disabled={editLoading}
                          >
                            {`{{${ph.key}}}`}
                          </button>
                          <span className="text-xs text-blue-700">{ph.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTemplate(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-300"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition duration-300 disabled:opacity-50"
                  disabled={editLoading || !editFormData.name.trim() || !editFormData.subject.trim() || !editFormData.body.trim()}
                >
                  {editLoading ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

export default EmailTemplatesSettingsPage;
